import * as amplitude from "@amplitude/analytics-browser";
import { LogLevel } from "@amplitude/analytics-types";
import { plugin as engagementPlugin } from "@amplitude/engagement-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  ColorModeScript,
} from "@chakra-ui/react";
import "@fontsource/inter";
import * as Integrations from "@sentry/integrations";
import * as Sentry from "@sentry/react";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import TagManager from "react-gtm-module";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import reportWebVitals from "./reportWebVitals";
import App from "./scenes/App";
import * as serviceWorker from "./serviceWorker";
import { captureUTMParams } from "./utils/utmTracking";

// Capture UTM parameters before any authentication redirects
captureUTMParams();

const container = document.getElementById("root");
const root = createRoot(container!);

if (import.meta.env.VITE_AMPLITUDE_KEY) {
  const sessionReplayTracking = sessionReplayPlugin({
    // Stay within ~10k session replays/mo (plan limit) at ~5k DAU; raise if consistently under quota.
    sampleRate: 0.05,
  });
  amplitude.add(sessionReplayTracking);
  amplitude.init(import.meta.env.VITE_AMPLITUDE_KEY, {
    autocapture: {
      attribution: true,
      pageViews: true,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },
  });
  amplitude.add(engagementPlugin());
} else {
  if (import.meta.env.VITE_ENABLE_AMPLITUDE_LOCAL_DEBUG) {
    amplitude
      .init("", undefined, {
        logLevel: LogLevel.Debug,
      })
      .promise.then(() => {});
  }
}

if (
  ["qa", "uat", "prod"].includes(import.meta.env.VITE_ENVIRONMENT ?? "") &&
  process.env.NODE_ENV !== "development"
) {
  Sentry.init({
    dsn: "https://0c812c8cee5d4b2bbf82fe8f0b13a9a1@o4504255885344768.ingest.sentry.io/4504255888293888",
    integrations: [
      new Sentry.BrowserTracing({
        // See docs for support of different versions of variation of react router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        routingInstrumentation: Sentry.reactRouterV6Instrumentation(
          React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        ),
      }),
      new Sentry.Replay(),
      new Integrations.CaptureConsole({ levels: ["error"] }),
    ],
    environment: import.meta.env.VITE_ENVIRONMENT ?? "local", // set to local by default since when running locally, you'll likely comment out the if check.

    tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === "prod" ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
  });
}

if (import.meta.env.VITE_GOOGLE_TAG_MANAGER_ENABLED === "true") {
  const tagManagerArgs = {
    gtmId: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
    auth: import.meta.env.VITE_GOOGLE_TAG_MANAGER_AUTH_ID,
    preview: import.meta.env.VITE_GOOGLE_TAG_MANAGER_PREVIEW_ID,
  };

  TagManager.initialize(tagManagerArgs);
}

root.render(
  <ErrorBoundary
    fallback={
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Error</AlertDescription>
      </Alert>
    }
  >
    <React.StrictMode>
      <ColorModeScript />
      <App />
    </React.StrictMode>
  </ErrorBoundary>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorker.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
